import React, { useState } from 'react';
import styled from 'styled-components';
import { format, isValid, parseISO } from 'date-fns';
import Paginate from '../../../components/Paginate';

const PAGE_SIZE = 25;
const COLUMNS = 3;

const formatDate = (value) => {
  const date = parseISO(value);
  return isValid(date) ? format(date, 'dd/MM/yyyy HH:mm') : '';
};

// Lists the documents a bulk reparse would touch, together with the search summary (total,
// empty and truncated states). The gateway already caps `items`, so pagination happens here,
// client-side. Page state starts at 0 on every mount: the parent only renders this once the
// search settles, so each new search gets a fresh instance.
export default function BulkReparseCandidatesTable({ items, count, truncated }) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const pageItems = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const onPageChange = (delta) => setPage((current) => current + delta);

  return (
    <div className="mt-3">
      <TableWrapper>
        <Table>
          <thead>
            {count > 0 && (
              <tr>
                <SummaryCell colSpan={COLUMNS}>
                  Se van a reparsear {count} documento{count === 1 ? '' : 's'}.
                </SummaryCell>
              </tr>
            )}
            <tr>
              <HeaderCell>Document ID</HeaderCell>
              <HeaderCell>Nombre</HeaderCell>
              <HeaderCell>Fecha</HeaderCell>
            </tr>
          </thead>
          <tbody>
            {count === 0 && (
              <tr>
                <MessageCell colSpan={COLUMNS}>
                  No hay documentos que coincidan con estos filtros.
                </MessageCell>
              </tr>
            )}
            {pageItems.map((item) => (
              <tr key={item.parsingJobId}>
                <Cell>
                  <DocumentLink
                    href={`/support/document_trace?documentId=${encodeURIComponent(item.scrapedDocumentId)}`}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {item.scrapedDocumentId}
                  </DocumentLink>
                </Cell>
                <Cell>{item.documentName}</Cell>
                <Cell>{formatDate(item.date)}</Cell>
              </tr>
            ))}
          </tbody>
          {truncated && (
            <tfoot>
              <tr>
                <WarningCell colSpan={COLUMNS}>
                  Mostrando los primeros {items.length} de {count} documentos.
                </WarningCell>
              </tr>
            </tfoot>
          )}
        </Table>
      </TableWrapper>
      {count > 0 && (
        <>
          <div className="mt-2 text-center">
            Página {page + 1} de {pageCount}
          </div>
          <Paginate
            disableNext={page + 1 >= pageCount}
            disablePrev={page === 0}
            onPageChange={onPageChange}
          />
        </>
      )}
    </div>
  );
}

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Cell = styled.td`
  border-bottom: 1px solid #e1e5eb;
  padding: 8px;
  vertical-align: middle;
  overflow-wrap: anywhere;
`;

const HeaderCell = styled(Cell).attrs({ as: 'th' })`
  font-weight: 600;
  background: #f8f9fa;
`;

const SummaryCell = styled(HeaderCell)`
  font-weight: 500;
`;

const MessageCell = styled(Cell)`
  text-align: center;
`;

const WarningCell = styled(Cell)`
  background: #fff8e1;
`;

const DocumentLink = styled.a`
  font-family: monospace;
  font-size: 12px;
`;
